import { CLEAR_FLAG } from "@/lib/clear-flag";

export type Prefixed<T, P extends string> = {
  [K in keyof T as K extends string ? `${P}${K}` : never]: T[K];
};
export const createPrefix =
  <T extends Record<any, any>>() =>
  <P extends string>(prefix: P) => {
    // The router's clear flag means nothing under a prefix -- retainSearchParamsWithClear only reads
    // it un-prefixed. Renamed to `<prefix>__clear`, it used to stay in the URL for good and come
    // back out of `extract` as a plain `__clear` inside the table argument (ID-7067). So it is
    // dropped on the way out, ignored on the way in, and a URL that still carries the renamed key
    // (a bookmark or shared link from before) gets it written as undefined: every caller spreads
    // this output over the current search, so the table's next navigation removes it.
    const prefixedClearFlag = `${prefix}${CLEAR_FLAG}`;
    const forward = (data: T) =>
      Object.fromEntries([
        ...Object.entries(data)
          .filter(([k]) => k !== CLEAR_FLAG)
          .map(([k, v]) => [`${prefix}${k}`, v]),
        [prefixedClearFlag, undefined],
      ]) as Prefixed<T, P>;
    const rxp = new RegExp(`(${prefix})(.*)`);
    const backward = (data: T) =>
      Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k.replace(rxp, "$2"), v]),
      ) as Prefixed<T, P>;
    const extract = (data: any) =>
      Object.fromEntries(
        Object.entries(data)
          .filter(([k]) => k.startsWith(prefix) && k !== prefixedClearFlag)
          .map(([k, v]) => [k.replace(rxp, "$2"), v]),
      ) as T;
    return { forward, backward, extract };
  };

type Mapped<T extends Record<any, any>, M extends Record<keyof T, any>> = {
  [K in keyof T]: M[K];
};
export const createMapping =
  <T extends Record<any, any>>() =>
  <M extends Record<keyof T, any>>(mapping: M) => {
    const reverse = Object.fromEntries(
      Object.entries(mapping).map(([k, v]) => [v, k]),
    ) as M extends Record<infer K, infer V>
      ? V extends string | number | symbol
        ? Record<V, K>
        : never
      : never;
    const forward = (data: T) =>
      Object.fromEntries(
        Object.entries(data).map(([k, v]) => [mapping[k] ?? k, v]),
      ) as Mapped<T, M>;
    const backward = (data: T) =>
      Object.fromEntries(
        Object.entries(data).map(([k, v]) => [reverse[k] ?? k, v]),
      ) as Mapped<T, M>;
    const extract = (data: T) =>
      Object.fromEntries(
        Object.entries(data)
          .filter(([k]) => mapping[k])
          .map(([k, v]) => [reverse[k] ?? k, v]),
      ) as Mapped<T, M>;
    return { reverse, forward, backward, extract };
  };

export const createPicker =
  <T extends Record<any, any>>() =>
  <K extends keyof T>(keys: K[]) => {
    const forward = (d: T) => d as Pick<T, K>;
    const backward = (d: Pick<T, K>) => d as T;
    const extract = (data: T) =>
      Object.fromEntries(
        Object.entries(data).filter(([k]) => keys.includes(k as any)),
      ) as T;
    return { forward, backward, extract };
  };

export type Namespaced = ReturnType<ReturnType<typeof createPicker<any>>>;
